import { useState, useEffect } from 'react'
import {
  Box, Text, Button, TextInput, PasswordInput, NumberInput, Switch, Stack, Group,
  ActionIcon, Tooltip, Divider, Loader, Center, Textarea, Tabs, SegmentedControl,
} from '@mantine/core'
import {
  IconArrowLeft, IconMail, IconSend, IconDeviceFloppy, IconTag, IconPlus, IconTrash,
} from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import { useNavigate } from 'react-router-dom'
import {
  getPlatformEmailSettings, updatePlatformEmailSettings, testPlatformEmail,
  PlatformEmailSettings, PlatformEmailProvider,
  getPlatformPricing, updatePlatformPricing,
} from '../../api/platformClient'

const CARD_BG = '#131a2c'
const CARD_BORDER = '1px solid #232c44'
const LABEL_COLOR = '#9aa3ba'

// ── หน้านี้ตั้งใจแยกเป็น "หมวด" ด้วย Tabs ตั้งแต่ต้น (ไม่ใช่ flat page เดียวแบบเดิม) เพราะมีแผน
// จะเพิ่มหมวดอื่นๆ ต่อในอนาคต — ตอนจะเพิ่มหมวดใหม่ ทำ 2 จุด: (1) เพิ่ม <Tabs.Tab value="..."> ใน
// Tabs.List ด้านล่าง (2) เพิ่ม <Tabs.Panel value="..."> คู่กัน แต่ละหมวดควรเก็บ state/logic ของ
// ตัวเองแยกเป็นไฟล์ย่อย (ดูตัวอย่าง EmailSettingsTab ด้านล่าง) ไม่ผูกรวมกับหมวดอื่นเหมือนที่เคยทำ ──
//
// หมายเหตุสำคัญ: หมวด "Pricing" ด้านล่าง (PricingSettingsTab) เป็นแค่ "เนื้อหาราคาโฆษณา" ที่โชว์
// หน้าเว็บขาย (www.assetsservice.com) เท่านั้น — ไม่ใช่ระบบ "Plan/Entitlement" ที่คุยกันไว้ก่อน
// หน้านี้ว่าจะใช้ผูกสิทธิ์การสร้าง Application แยกชุดอื่นๆ (Network Scanner, Computer Monitoring
// ฯลฯ) ในอนาคต — ตอนจะสร้างระบบ Plan/Entitlement จริงๆ ควรเป็นอีกหมวดแยกต่างหาก (เช่น value=
// "entitlements") ไม่ควรเอามารวมกับหมวดนี้เพราะคนละเรื่องกันโดยสิ้นเชิง ──
export default function PlatformSettingsPage() {
  const navigate = useNavigate()

  return (
    <Box style={{ minHeight: '100vh', background: '#0a0e17', fontFamily: 'Inter, sans-serif' }}>
      {/* Header */}
      <Box style={{ borderBottom: CARD_BORDER, padding: '18px 32px' }}>
        <Group gap={10}>
          <Tooltip label="Back to tenants">
            <ActionIcon variant="subtle" color="gray" onClick={() => navigate('/platform/dashboard')}>
              <IconArrowLeft size={17} />
            </ActionIcon>
          </Tooltip>
          <Text style={{ color: '#fff', fontSize: 16, fontWeight: 700 }}>Platform Settings</Text>
        </Group>
      </Box>

      <Box style={{ padding: '28px 32px', maxWidth: 1100, margin: '0 auto' }}>
        <Tabs defaultValue="email" color="orange">
          <Tabs.List mb={20}>
            <Tabs.Tab value="email" leftSection={<IconMail size={15} />}>Email (SMTP)</Tabs.Tab>
            <Tabs.Tab value="pricing" leftSection={<IconTag size={15} />}>Pricing</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="email">
            <EmailSettingsTab />
          </Tabs.Panel>

          <Tabs.Panel value="pricing">
            <PricingSettingsTab />
          </Tabs.Panel>
        </Tabs>
      </Box>
    </Box>
  )
}

// ── หมวด "Email (SMTP)" — แยกเป็น component ของตัวเอง เก็บ state/logic เฉพาะของหมวดนี้เอง
// ไม่ปนกับหมวดอื่นที่จะเพิ่มทีหลัง (เช่น Plan) ให้แต่ละหมวดแก้ไข/ทดสอบแยกจากกันได้อิสระ ──
function EmailSettingsTab() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [settings, setSettings] = useState<PlatformEmailSettings | null>(null)

  // ── form state — smtpPassword แยกจาก settings.hasPassword เสมอ: เว้นว่าง = ไม่แตะรหัสผ่านเดิม
  // (ดู comment ใน platform-settings.service.ts ฝั่ง backend เรื่อง undefined vs '' vs ค่าจริง) ──
  const [form, setForm] = useState({
    provider: 'smtp' as PlatformEmailProvider,
    smtpHost: '', smtpPort: 587, smtpSecure: false, smtpUser: '', smtpPassword: '',
    resendApiKey: '',
    fromName: '', fromAddress: '', notifyEmails: '',
  })
  const [testEmail, setTestEmail] = useState('')

  const loadAll = async () => {
    setLoading(true)
    try {
      const { data } = await getPlatformEmailSettings()
      setSettings(data)
      setForm({
        provider: data.provider ?? 'smtp',
        smtpHost: data.smtpHost ?? '',
        smtpPort: data.smtpPort ?? 587,
        smtpSecure: data.smtpSecure,
        smtpUser: data.smtpUser ?? '',
        smtpPassword: '', // ไม่เคยเติมรหัสผ่านจริงกลับเข้าฟอร์ม — backend ไม่คืนค่ามาให้ตั้งแต่แรก
        resendApiKey: '', // เหมือนกัน — ไม่เคยคืน API key จริงกลับมา
        fromName: data.fromName ?? '',
        fromAddress: data.fromAddress ?? '',
        notifyEmails: data.notifyEmails ?? '',
      })
    } catch {
      notifications.show({ color: 'red', message: 'Failed to load settings' })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadAll() }, [])

  const handleSave = async () => {
    setSaving(true)
    try {
      const { data } = await updatePlatformEmailSettings({
        provider: form.provider,
        smtpHost: form.smtpHost || null,
        smtpPort: form.smtpPort || null,
        smtpSecure: form.smtpSecure,
        smtpUser: form.smtpUser || null,
        // ── ส่ง smtpPassword/resendApiKey ก็ต่อเมื่อผู้ใช้พิมพ์อะไรจริงๆ เท่านั้น (ไม่ส่ง key นี้
        // เลยถ้าเว้นว่าง) เพื่อไม่ให้ backend ตีความว่า "ตั้งใจลบทิ้ง" ทั้งที่แค่เปิดหน้ามาดูเฉยๆ ──
        ...(form.smtpPassword ? { smtpPassword: form.smtpPassword } : {}),
        ...(form.resendApiKey ? { resendApiKey: form.resendApiKey } : {}),
        fromName: form.fromName || null,
        fromAddress: form.fromAddress || null,
        notifyEmails: form.notifyEmails || null,
      })
      setSettings(data)
      setForm((f) => ({ ...f, smtpPassword: '', resendApiKey: '' }))
      notifications.show({ color: 'green', message: 'Email settings saved' })
    } catch (err: any) {
      notifications.show({ color: 'red', message: err?.response?.data?.message ?? 'Failed to save settings' })
    } finally {
      setSaving(false)
    }
  }

  const handleTest = async () => {
    if (!testEmail) {
      notifications.show({ color: 'red', message: 'Enter an email address to send the test to' })
      return
    }
    setTesting(true)
    try {
      await testPlatformEmail(testEmail)
      notifications.show({ color: 'green', message: `Test email sent to ${testEmail}` })
    } catch (err: any) {
      notifications.show({ color: 'red', message: err?.response?.data?.message ?? 'Test email failed to send — check SMTP settings' })
    } finally {
      setTesting(false)
    }
  }

  if (loading) {
    return <Center p={60}><Loader color="orange" /></Center>
  }

  return (
    <Box style={{ background: CARD_BG, border: CARD_BORDER, borderRadius: 12, padding: 24 }}>
      <Group gap={8} mb={4}>
        <IconMail size={17} color="#f5a623" />
        <Text style={{ color: '#fff', fontSize: 15, fontWeight: 700 }}>Email Settings</Text>
      </Group>
      <Text style={{ color: LABEL_COLOR, fontSize: 12.5, marginBottom: 18 }}>
        Platform-wide — not per tenant. Used to send demo/contact requests from the sales site
        and any platform-level notifications. Secrets are encrypted at rest and never shown again after saving.
      </Text>

      <Stack gap={14}>
        <Box>
          <Text style={{ color: '#e5e7eb', fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Provider</Text>
          <SegmentedControl
            value={form.provider}
            onChange={(v) => setForm((f) => ({ ...f, provider: v as PlatformEmailProvider }))}
            data={[
              { label: 'SMTP', value: 'smtp' },
              { label: 'Resend', value: 'resend' },
            ]}
            color="orange"
            styles={{ root: { background: '#0f1524', border: '1px solid #232c44' }, label: { color: '#e5e7eb' } }}
          />
        </Box>

        {form.provider === 'resend' ? (
          <PasswordInput
            label="Resend API Key"
            description="From resend.com/api-keys — starts with re_"
            placeholder={settings?.hasResendApiKey ? '•••••••• (set — leave blank to keep)' : 're_xxxxxxxxxxxxxxxx'}
            value={form.resendApiKey}
            onChange={(e) => { const v = e.currentTarget.value; setForm((f) => ({ ...f, resendApiKey: v })) }}
            styles={inputStyles}
          />
        ) : (
          <>
            <Group grow>
              <TextInput
                label="SMTP Host"
                placeholder="smtp.gmail.com"
                value={form.smtpHost}
                onChange={(e) => { const v = e.currentTarget.value; setForm((f) => ({ ...f, smtpHost: v })) }}
                styles={inputStyles}
              />
              <NumberInput
                label="Port"
                placeholder="587"
                value={form.smtpPort}
                onChange={(v) => setForm((f) => ({ ...f, smtpPort: Number(v) || 587 }))}
                styles={inputStyles}
              />
            </Group>

            <Group grow align="flex-end">
              <TextInput
                label="Email (SMTP Username)"
                placeholder="noreply@assetsservice.com"
                value={form.smtpUser}
                onChange={(e) => { const v = e.currentTarget.value; setForm((f) => ({ ...f, smtpUser: v })) }}
                styles={inputStyles}
              />
              <PasswordInput
                label="Password"
                placeholder={settings?.hasPassword ? '•••••••• (set — leave blank to keep)' : 'Not set'}
                value={form.smtpPassword}
                onChange={(e) => { const v = e.currentTarget.value; setForm((f) => ({ ...f, smtpPassword: v })) }}
                styles={inputStyles}
              />
            </Group>

            <Switch
              label="Use SSL/TLS (secure)"
              checked={form.smtpSecure}
              onChange={(e) => { const checked = e.currentTarget.checked; setForm((f) => ({ ...f, smtpSecure: checked })) }}
              color="orange"
              styles={{ label: { color: '#e5e7eb', fontSize: 13 } }}
            />
          </>
        )}

        <Divider color="#232c44" my={4} />

        <Group grow>
          <TextInput
            label="From Name"
            placeholder="AssetOS"
            value={form.fromName}
            onChange={(e) => { const v = e.currentTarget.value; setForm((f) => ({ ...f, fromName: v })) }}
            styles={inputStyles}
          />
          <TextInput
            label="From Address"
            placeholder="noreply@assetsservice.com"
            value={form.fromAddress}
            onChange={(e) => { const v = e.currentTarget.value; setForm((f) => ({ ...f, fromAddress: v })) }}
            styles={inputStyles}
          />
        </Group>

        <Textarea
          label="Demo request recipient(s)"
          description="Where demo/contact form submissions from www.assetsservice.com get sent. Separate multiple addresses with commas."
          placeholder="info@assetsservice.com, sales@assetsservice.com"
          value={form.notifyEmails}
          onChange={(e) => { const v = e.currentTarget.value; setForm((f) => ({ ...f, notifyEmails: v })) }}
          autosize minRows={2}
          styles={inputStyles}
        />

        <Group justify="space-between" mt={8}>
          <Group gap={8}>
            <TextInput
              placeholder="Send a test email to..."
              value={testEmail}
              onChange={(e) => setTestEmail(e.currentTarget.value)}
              styles={{ input: { background: '#0f1524', border: '1px solid #232c44', color: '#fff', width: 240 } }}
            />
            <Button
              leftSection={<IconSend size={14} />}
              variant="default"
              size="sm"
              loading={testing}
              onClick={handleTest}
            >
              Send Test
            </Button>
          </Group>

          <Button
            leftSection={<IconDeviceFloppy size={14} />}
            loading={saving}
            onClick={handleSave}
            styles={{ root: { background: 'linear-gradient(135deg, #f5a623, #d98c1a)', color: '#0a0e17', fontWeight: 700 } }}
          >
            Save Settings
          </Button>
        </Group>
      </Stack>
    </Box>
  )
}

const inputStyles = {
  label: { color: '#e5e7eb', fontSize: 13, marginBottom: 4 },
  description: { color: LABEL_COLOR, fontSize: 11.5 },
  input: { background: '#0f1524', border: '1px solid #232c44', color: '#fff' },
}

// ── หมวด "Pricing" — เนื้อหาราคาแพ็กเกจที่โชว์บนหน้าเว็บขาย (www.assetsservice.com) แก้ที่นี่
// แล้วหน้าเว็บอัปเดตทันที ไม่ต้อง deploy frontend ใหม่ (ดึงผ่าน GET /public/pricing) ──
function PricingSettingsTab() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    monthlyPrice: 299, annualOriginalPrice: 3500, annualPrice: 1990,
    currency: 'THB', features: [''],
  })

  const loadAll = async () => {
    setLoading(true)
    try {
      const { data } = await getPlatformPricing()
      setForm({
        monthlyPrice: data.monthlyPrice,
        annualOriginalPrice: data.annualOriginalPrice,
        annualPrice: data.annualPrice,
        currency: data.currency,
        features: data.features.length ? data.features : [''],
      })
    } catch {
      notifications.show({ color: 'red', message: 'Failed to load pricing settings' })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadAll() }, [])

  const handleSave = async () => {
    setSaving(true)
    try {
      await updatePlatformPricing({
        monthlyPrice: form.monthlyPrice,
        annualOriginalPrice: form.annualOriginalPrice,
        annualPrice: form.annualPrice,
        currency: form.currency,
        features: form.features.map((f) => f.trim()).filter(Boolean),
      })
      notifications.show({ color: 'green', message: 'Pricing saved — the sales site updates immediately' })
    } catch (err: any) {
      notifications.show({ color: 'red', message: err?.response?.data?.message ?? 'Failed to save pricing' })
    } finally {
      setSaving(false)
    }
  }

  const updateFeature = (i: number, value: string) => {
    setForm((f) => ({ ...f, features: f.features.map((x, idx) => (idx === i ? value : x)) }))
  }
  const addFeature = () => setForm((f) => ({ ...f, features: [...f.features, ''] }))
  const removeFeature = (i: number) => setForm((f) => ({ ...f, features: f.features.filter((_, idx) => idx !== i) }))

  if (loading) {
    return <Center p={60}><Loader color="orange" /></Center>
  }

  const savePct = form.annualOriginalPrice > 0
    ? Math.round((1 - form.annualPrice / form.annualOriginalPrice) * 100)
    : 0

  return (
    <Box style={{ background: CARD_BG, border: CARD_BORDER, borderRadius: 12, padding: 24 }}>
      <Group gap={8} mb={4}>
        <IconTag size={17} color="#f5a623" />
        <Text style={{ color: '#fff', fontSize: 15, fontWeight: 700 }}>Pricing — Sales Site</Text>
      </Group>
      <Text style={{ color: LABEL_COLOR, fontSize: 12.5, marginBottom: 18 }}>
        Controls the price and feature bullets shown on the Pricing section of www.assetsservice.com.
        Saving here updates the live site immediately — no redeploy needed.
      </Text>

      <Stack gap={18}>
        <Group grow align="flex-start">
          <NumberInput
            label="Monthly price"
            description="Shown as a plain monthly rate — no discount badge"
            value={form.monthlyPrice}
            onChange={(v) => setForm((f) => ({ ...f, monthlyPrice: Number(v) || 0 }))}
            styles={inputStyles}
          />
          <NumberInput
            label="Annual — original price"
            description="Shown struck through next to the discounted price"
            value={form.annualOriginalPrice}
            onChange={(v) => setForm((f) => ({ ...f, annualOriginalPrice: Number(v) || 0 }))}
            styles={inputStyles}
          />
          <NumberInput
            label="Annual — discounted price"
            description={`What the customer actually pays/year${savePct > 0 ? ` — shows "save ${savePct}%"` : ''}`}
            value={form.annualPrice}
            onChange={(v) => setForm((f) => ({ ...f, annualPrice: Number(v) || 0 }))}
            styles={inputStyles}
          />
        </Group>

        <TextInput
          label="Currency code"
          description="Displayed as-is next to the price (e.g. THB)"
          value={form.currency}
          onChange={(e) => { const v = e.currentTarget.value; setForm((f) => ({ ...f, currency: v })) }}
          style={{ maxWidth: 200 }}
          styles={inputStyles}
        />

        <Divider color="#232c44" my={4} />

        <Box>
          <Text style={{ color: '#e5e7eb', fontSize: 13, fontWeight: 500, marginBottom: 10 }}>
            Feature bullets (shown under the price)
          </Text>
          <Stack gap={8}>
            {form.features.map((feature, i) => (
              <Group key={i} gap={8} wrap="nowrap">
                <TextInput
                  value={feature}
                  onChange={(e) => { const v = e.currentTarget.value; updateFeature(i, v) }}
                  placeholder="e.g. Always-on access from web, desktop and mobile"
                  style={{ flex: 1 }}
                  styles={inputStyles}
                />
                <ActionIcon color="red" variant="subtle" onClick={() => removeFeature(i)} disabled={form.features.length <= 1}>
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            ))}
          </Stack>
          <Button leftSection={<IconPlus size={14} />} variant="default" size="xs" mt={10} onClick={addFeature}>
            Add bullet
          </Button>
        </Box>

        <Group justify="flex-end" mt={8}>
          <Button
            leftSection={<IconDeviceFloppy size={14} />}
            loading={saving}
            onClick={handleSave}
            styles={{ root: { background: 'linear-gradient(135deg, #f5a623, #d98c1a)', color: '#0a0e17', fontWeight: 700 } }}
          >
            Save Pricing
          </Button>
        </Group>
      </Stack>
    </Box>
  )
}
